/**
 * Supported mobile countries — the SINGLE source of truth for the country list
 * and per-country national-number rules used by surfaces that accept non-Saudi
 * mobiles (today: the public "register interest" capture, which verifies the
 * phone with an SMS code before creating a lead).
 *
 * Consumers:
 *   - SPA: `src/lib/phoneCountries.ts` re-exports this module (PhoneInput's
 *     `countries` dropdown + InterestForm validation).
 *   - API: `apps/api/src/common/phone/supported-mobile-countries.ts` mirrors the
 *     VALUES locally (apps/api may not runtime-import @dallali/shared — see
 *     tools/ci/check-api-shared-import.mjs) and its spec pins parity with this
 *     file, so the two cannot drift.
 *
 * Each entry pins the dial code and the exact number of national digits after
 * it. Values travel as E.164 (`+<dial><national>`), the shape Unifonic and the
 * API expect.
 *
 * NOTE: login / owner / tenant OTP surfaces stay Saudi-only (`IsSaudiPhone`,
 * `+9665XXXXXXXX`) — they keep using `PhoneInput` without a `countries` list.
 */
export type PhoneCountryCode = "SA" | "AE" | "KW" | "BH" | "QA" | "OM" | "EG" | "JO";

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2. */
  code: PhoneCountryCode;
  dialCode: string;
  flag: string;
  /** Exact number of digits after the dial code. */
  nationalLength: number;
  /** Required leading digit(s) of the national number, if any. */
  startsWith?: string;
  /** Placeholder shown in the input. */
  example: string;
}

export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { code: "SA", dialCode: "+966", flag: "\u{1F1F8}\u{1F1E6}", nationalLength: 9, startsWith: "5", example: "5XXXXXXXX" },
  { code: "AE", dialCode: "+971", flag: "\u{1F1E6}\u{1F1EA}", nationalLength: 9, example: "XXXXXXXXX" },
  { code: "KW", dialCode: "+965", flag: "\u{1F1F0}\u{1F1FC}", nationalLength: 8, example: "XXXXXXXX" },
  { code: "BH", dialCode: "+973", flag: "\u{1F1E7}\u{1F1ED}", nationalLength: 8, example: "XXXXXXXX" },
  { code: "QA", dialCode: "+974", flag: "\u{1F1F6}\u{1F1E6}", nationalLength: 8, example: "XXXXXXXX" },
  { code: "OM", dialCode: "+968", flag: "\u{1F1F4}\u{1F1F2}", nationalLength: 8, example: "XXXXXXXX" },
  { code: "EG", dialCode: "+20", flag: "\u{1F1EA}\u{1F1EC}", nationalLength: 10, example: "XXXXXXXXXX" },
  { code: "JO", dialCode: "+962", flag: "\u{1F1EF}\u{1F1F4}", nationalLength: 9, example: "XXXXXXXXX" },
];

export const SAUDI_PHONE_COUNTRY: PhoneCountry = PHONE_COUNTRIES[0];

export function findPhoneCountryByDial(
  dialCode: string | null | undefined,
  countries: readonly PhoneCountry[] = PHONE_COUNTRIES,
): PhoneCountry | undefined {
  return countries.find((c) => c.dialCode === dialCode);
}

/** Digits-only national number valid for the country (length + leading digit). */
export function isValidNationalNumber(country: PhoneCountry, digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  if (digits.length !== country.nationalLength) return false;
  return !country.startsWith || digits.startsWith(country.startsWith);
}

export function toE164(country: PhoneCountry, digits: string): string {
  return `${country.dialCode}${digits}`;
}

/**
 * Resolve the country of a full E.164 value. Longest dial code wins so "+20…"
 * can never shadow a longer prefix.
 */
export function findPhoneCountryForE164(
  value: string | null | undefined,
  countries: readonly PhoneCountry[] = PHONE_COUNTRIES,
): PhoneCountry | undefined {
  if (!value) return undefined;
  return [...countries]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((c) => value.startsWith(c.dialCode));
}

/** Validate a full E.164 value (`+<dial><national>`) against the catalogue. */
export function isValidE164ForCountries(
  value: string | null | undefined,
  countries: readonly PhoneCountry[] = PHONE_COUNTRIES,
): boolean {
  const match = findPhoneCountryForE164(value, countries);
  if (!match || !value) return false;
  return isValidNationalNumber(match, value.slice(match.dialCode.length));
}
